import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiArrowLeft,
  mdiPhone,
  mdiMapMarker,
  mdiStar,
  mdiPencil,
  mdiOpenInNew,
} from "@mdi/js";
import { Chip, Tooltip } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import { serviceCenterFormStore } from "@/stores/ServiceCenterFormStore";
import { ROUTES } from "@/constants/routes";
import { BUSINESS_TYPE_LABEL } from "../constants";

/** Скрол-порог в px: после него переключатель года прячется */
const SCROLL_HIDE_THRESHOLD = 300;

type YearNavPhase = "visible" | "exiting" | "hidden" | "entering";

const ContractorHeader: React.FC = observer(() => {
  const navigate = useNavigate();
  const contractor = contractorDetailStore.contractor;
  const { statsYear, setStatsYear } = contractorDetailStore;
  const currentYear = new Date().getFullYear();
  const yearOptions = [statsYear - 1, statsYear, statsYear + 1];

  const [phase, setPhase] = useState<YearNavPhase>("visible");
  const phaseTimer = useRef<number>(0);

  useEffect(() => {
    if (!contractor) return;
    const container = document.querySelector(
      ".app-layout__content",
    ) as HTMLElement | null;
    if (!container) return;

    const onScroll = () => {
      const past = container.scrollTop > SCROLL_HIDE_THRESHOLD;
      setPhase((current) => {
        if (past && (current === "visible" || current === "entering")) {
          clearTimeout(phaseTimer.current);
          phaseTimer.current = window.setTimeout(() => setPhase("hidden"), 240);
          return "exiting";
        }
        if (!past && (current === "hidden" || current === "exiting")) {
          clearTimeout(phaseTimer.current);
          phaseTimer.current = window.setTimeout(
            () => setPhase("visible"),
            360,
          );
          return "entering";
        }
        return current;
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      clearTimeout(phaseTimer.current);
    };
  }, [contractor]);

  if (!contractor) return null;

  const yearNavClass =
    phase === "exiting"
      ? "scd-year-nav scd-year-nav--exiting"
      : phase === "hidden"
        ? "scd-year-nav scd-year-nav--gone"
        : phase === "entering"
          ? "scd-year-nav scd-year-nav--entering"
          : "scd-year-nav";

  return (
    <div className="scd-header" data-testid="contractor-detail-header">
      <div className="scd-header__info">
        <div className="scd-header__top-row">
          <button
            type="button"
            className="scd-back"
            onClick={() => navigate(ROUTES.CONTRACTORS)}
            aria-label="К списку контрагентов"
            data-testid="contractor-detail-back-button"
          >
            <Icon path={mdiArrowLeft} size={0.7} />
          </button>
          <Tooltip delay={400}>
            <Tooltip.Trigger>
              <h1 className="scd-header__name" tabIndex={0}>
                {contractor.name}
              </h1>
            </Tooltip.Trigger>
            <Tooltip.Content>{contractor.name}</Tooltip.Content>
          </Tooltip>
          <div className="scd-header__badges">
            <Chip
              color={contractor.isPublished ? "success" : "danger"}
              variant="soft"
              size="sm"
            >
              {contractor.isPublished ? "Активен" : "Неактивен"}
            </Chip>
            {contractor.businessTypes?.map((bt) => (
              <Chip key={bt} variant="soft" size="sm" color="default">
                {BUSINESS_TYPE_LABEL[bt] ?? bt}
              </Chip>
            ))}
            <button
              className="scd-edit-btn"
              aria-label="Войти в панель СЦ"
              title="Войти в панель СЦ"
              disabled={contractorDetailStore.isImpersonating}
              onClick={() => contractorDetailStore.openServiceCenterPanel()}
              data-testid="contractor-detail-open-panel-button"
            >
              <Icon path={mdiOpenInNew} size={0.67} />
            </button>
            <button
              className="scd-edit-btn"
              aria-label="Редактировать"
              onClick={() => serviceCenterFormStore.openEdit(contractor)}
              data-testid="contractor-detail-edit-button"
            >
              <Icon path={mdiPencil} size={0.67} />
            </button>
          </div>
        </div>
        <div className="scd-header__details">
          <span className="scd-header__detail">
            <Icon path={mdiMapMarker} size={0.65} />
            {contractor.address}
          </span>
          {(contractor.contacts as { phone?: string })?.phone && (
            <span className="scd-header__detail">
              <Icon path={mdiPhone} size={0.65} />
              {(contractor.contacts as { phone?: string }).phone}
            </span>
          )}
          {contractor.rating != null && (
            <span className="scd-header__detail">
              <Icon path={mdiStar} size={0.65} />
              {Number(contractor.rating).toFixed(1)}
            </span>
          )}
          <span className="scd-header__detail scd-header__detail--muted">
            Постов: {contractor.postsCount ?? 0}
          </span>
          {contractor.created_at && (
            <span className="scd-header__detail scd-header__detail--muted">
              Регистрация:{" "}
              {new Date(contractor.created_at).toLocaleDateString("ru-RU")}
            </span>
          )}
        </div>
      </div>

      <div
        className={yearNavClass}
        aria-hidden={phase === "hidden" || phase === "exiting"}
        data-testid="contractor-detail-year-nav"
      >
        <button
          type="button"
          className="scd-year-nav__arrow"
          onClick={() => setStatsYear(statsYear - 1)}
          aria-label="Предыдущий год"
        >
          ‹
        </button>
        {yearOptions.map((y) => (
          <button
            key={y}
            type="button"
            className={`scd-year-nav__btn${statsYear === y ? " scd-year-nav__btn--active" : ""}`}
            onClick={() => y <= currentYear && setStatsYear(y)}
            disabled={y > currentYear}
            aria-disabled={y > currentYear}
          >
            {y}
          </button>
        ))}
        <button
          type="button"
          className="scd-year-nav__arrow"
          onClick={() => setStatsYear(Math.min(statsYear + 1, currentYear))}
          disabled={statsYear >= currentYear}
          aria-label="Следующий год"
        >
          ›
        </button>
      </div>
    </div>
  );
});

ContractorHeader.displayName = "ContractorHeader";
export default ContractorHeader;
